import React, { useState, useRef } from 'react';
import { Mic, Square, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function VoiceMicButton({ onTranscript, className, size = 'default' }) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorder.current = recorder;
      chunks.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks.current, { type: 'audio/webm' });
        if (blob.size < 100) {
          toast.error('Recording too short');
          return;
        }
        await transcribe(blob);
      };

      recorder.start();
      setRecording(true);
    } catch {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      mediaRecorder.current.stop();
    }
    setRecording(false);
  };

  const transcribe = async (blob) => {
    setTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('audio', blob, 'recording.webm');
      const res = await fetch(`${API_URL}/api/voice/transcribe`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!res.ok) throw new Error('Transcription failed');
      const { text } = await res.json();
      if (text) {
        onTranscript(text);
      } else {
        toast.error('No speech detected');
      }
    } catch {
      toast.error('Voice transcription failed');
    } finally {
      setTranscribing(false);
    }
  };

  const isSmall = size === 'sm';
  const iconSize = isSmall ? 'w-3.5 h-3.5' : 'w-4 h-4';
  const btnSize = isSmall ? 'w-8 h-8' : 'w-10 h-10';

  if (transcribing) {
    return (
      <button
        type="button"
        disabled
        className={cn(btnSize, 'rounded-full flex items-center justify-center bg-zinc-100 text-zinc-400', className)}
        data-testid="voice-transcribing"
      >
        <Loader2 className={cn(iconSize, 'animate-spin')} />
      </button>
    );
  }

  if (recording) {
    return (
      <button
        type="button"
        onClick={stopRecording}
        className={cn(btnSize, 'rounded-full flex items-center justify-center bg-red-500 text-white animate-pulse', className)}
        data-testid="voice-stop-btn"
      >
        <Square className={isSmall ? 'w-3 h-3' : 'w-3.5 h-3.5'} fill="white" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={startRecording}
      className={cn(btnSize, 'rounded-full flex items-center justify-center bg-zinc-100 hover:bg-zinc-200 text-zinc-600 transition-colors', className)}
      data-testid="voice-mic-btn"
    >
      <Mic className={iconSize} />
    </button>
  );
}
